import { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

import Box from '~/common/components/Box';

import HighlightArea from './HighlightArea';

const options = {
  standardFontDataUrl: '/standard_fonts/',
  cMapUrl: '/cmaps/',
};

type PagesProps = {
  pdf?: any;
  scale?: any;
  highlights?: any;
  pageRotations?: any;
  pageNumber?: number;
  setCalculatedPageRotations?: any;
  onRenderSuccess?: any;
  onLoadSuccess?: any;
  onLoadError?: any;
  setPageRef?: any;
  clearPageRef?: any;
  citations?: any[];
  showHighLight?: boolean;
};

function Pages({
  pdf,
  scale,
  highlights,
  pageRotations,
  setCalculatedPageRotations,
  onRenderSuccess,
  onLoadSuccess,
  onLoadError,
  setPageRef,
  clearPageRef,
  citations,
  showHighLight,
}: Readonly<PagesProps>) {
  const [pageCount, setPageCount] = useState(0);
  const [key, setKey] = useState<string>();
  const calculatedPageRotationRef = useRef([]);
  const overlayStyle = {
    parent: {
      position: 'relative',
      width: 'auto',
      height: 'auto',
      marginBottom: '0.5rem',
    } as const,
    child: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 100,
      pointerEvents: 'none',
    } as const,
  };

  useEffect(() => {
    calculatedPageRotationRef.current = [];
    setCalculatedPageRotations([]);
    const key = crypto.randomUUID();
    setKey(key);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [pdf]);

  function getCalculatedPageRotation(number) {
    let rot = 0;
    if (
      calculatedPageRotationRef.current != null &&
      calculatedPageRotationRef.current.length > 0
    ) {
      const page = calculatedPageRotationRef.current.find(
        (pr) => pr.pageNumber == number,
      );
      if (page != null) {
        rot = page.rotationAngle;
      }
    }
    return rot;
  }

  function calculatePageRotation(page) {
    const originalRotation = page.rotate ?? 0;
    let AddedRotation = 0;
    if (pageRotations != null && pageRotations.length > 0) {
      const pageRot = pageRotations.find(
        (pr) => pr.pageNumber == page.pageNumber,
      );
      if (pageRot == null) {
        const otherPageRot = pageRotations.find(
          (pr) => pr !== null && pr !== undefined,
        );
        if (otherPageRot != null) {
          AddedRotation = otherPageRot.rotationAngle;
        }
      } else {
        AddedRotation = pageRot.rotationAngle;
      }
    }
    const calculatedRotation = AddedRotation + originalRotation;
    const calcPage = calculatedPageRotationRef.current.find(
      (pr) => pr.pageNumber == page.pageNumber,
    );
    if (calcPage == null) {
      calculatedPageRotationRef.current.push({
        pageNumber: page.pageNumber,
        rotationAngle: calculatedRotation,
      });
    } else {
      calcPage.rotationAngle = calculatedRotation;
    }
    setCalculatedPageRotations(calculatedPageRotationRef.current);
  }

  function createPages(n) {
    const pages = [];
    if (clearPageRef) {
      clearPageRef();
    }
    for (let i = 1; i <= n; i++) {
      pages.push(
        <Box
          key={i}
          sx={{ pb: '2px', width: 'fit-content' }}
          ref={(element) => {
            setPageRef(i, element);
          }}
        >
          <div style={overlayStyle.parent}>
            <Page
              renderTextLayer={false}
              onRenderSuccess={onRenderSuccess}
              renderAnnotationLayer={false}
              pageNumber={i}
              scale={scale}
              onLoadSuccess={handlePageLoadSuccess}
              rotate={getCalculatedPageRotation(i)}
            />

            {citations?.length > 0 && (
              <div style={overlayStyle.child}>
                <HighlightArea
                  highlights={highlights}
                  citations={citations}
                  pageNumber={i}
                  showHighLight={showHighLight}
                />
              </div>
            )}
          </div>
        </Box>,
      );
    }
    return pages;
  }

  function handlePageLoadSuccess(page) {
    calculatePageRotation(page);
  }

  function handleDocumentLoadSuccess(doc) {
    setPageCount(doc.numPages);
    onLoadSuccess(doc);
  }

  return (
    <Box sx={{ width: 'fit-content', margin: 'auto' }}>
      <Document
        key={key}
        options={options}
        file={pdf}
        loading={null}
        onLoadError={onLoadError}
        onLoadSuccess={handleDocumentLoadSuccess}
        onContextMenu={(e) => e.preventDefault()}
        noData={null}
      >
        {createPages(pageCount)}
      </Document>
    </Box>
  );
}

export default Pages;
